import React from 'react';
import { useDictionary } from '../hooks';
import { DictionaryEntry } from '../types';
import { Command } from 'lucide-react';

interface DictionaryHomeCyberpunkProps {
    onTermClick: (entry: DictionaryEntry) => void;
    searchInputRef: React.RefObject<HTMLInputElement>;
}

const getCardTheme = (index: number) => {
    const themes = ['cyan', 'fuchsia', 'violet'] as const;
    return themes[index % themes.length];
};

export default function DictionaryHomeCyberpunk({ onTermClick, searchInputRef }: DictionaryHomeCyberpunkProps) {
    const {
        data,
        loading,
        searchQuery,
        selectedTag,
        selectedLetter,
        filteredData,
        handleSearch,
        handleTagFilter,
        handleLetterFilter,
        getRandomEntry,
        totalCount,
        visibleCount
    } = useDictionary();

    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

    const tags = [...new Set(data.flatMap(entry => entry.tags))];

    const handleRandomClick = () => {
        const randomEntry = getRandomEntry();
        if (randomEntry) {
            onTermClick(randomEntry);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-6">
                <div className="relative">
                    <div className="w-16 h-16 border-4 border-cyan-900 rounded-full"></div>
                    <div className="w-16 h-16 border-4 border-transparent border-t-cyan-400 rounded-full animate-spin absolute top-0 left-0 shadow-[0_0_15px_rgba(34,211,238,0.5)]"></div>
                </div>
                <p className="text-cyan-400 font-mono italic text-xl animate-pulse">SYSTEM_LOADING...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-white font-sans relative overflow-hidden">
            {/* Animated gradient background */}
            <div className="fixed inset-0 opacity-30 pointer-events-none">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-fuchsia-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-violet-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Grid overlay */}
            <div className="fixed inset-0 pointer-events-none opacity-5" style={{
                backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.5) 1px, transparent 1px)',
                backgroundSize: '50px 50px'
            }}></div>

            {/* Header */}
            <header className="relative border-b border-cyan-500/30 backdrop-blur-sm bg-slate-950/80 sticky top-0 z-50">
                <div className="h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent"></div>

                <div className="max-w-7xl mx-auto px-6 py-8">
                    <div className="flex flex-col md:flex-row items-start justify-between gap-8 mb-8">
                        <div>
                            <h1 className="text-5xl md:text-7xl font-sans font-black leading-tight mb-2 tracking-tight">
                                <span className="bg-gradient-to-r from-cyan-400 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">
                                    Dictionnaire énervé
                                </span>
                                <span className="block text-3xl md:text-4xl mt-3 text-slate-300 font-sans font-light opacity-90">
                                    du Product Management
                                </span>
                            </h1>
                            <p className="text-lg text-slate-400 mt-6 underline decoration-cyan-500/30 underline-offset-8 font-sans font-light">
                                Le lexique décalé pour (sur)vivre en Product
                            </p>
                            <p className="text-sm text-slate-500 mt-10 font-mono">
                                <span className="text-cyan-400 animate-pulse">&gt;</span> Par Pierre-Marie Brou • Collection Framework & frustrations
                            </p>
                        </div>

                        <button
                            onClick={handleRandomClick}
                            className="group relative px-6 py-3 font-black overflow-hidden rounded-lg self-end md:self-start"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-fuchsia-500 opacity-60 group-hover:opacity-100 transition-opacity blur"></div>
                            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-fuchsia-500"></div>
                            <span className="relative text-white flex items-center gap-2">
                                <span className="text-lg">⚡</span>
                                ALÉATOIRE
                            </span>
                        </button>
                    </div>

                    {/* Search bar cyberpunk */}
                    <div className="relative group">
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-fuchsia-500 rounded-lg blur opacity-20 group-hover:opacity-40 transition duration-300"></div>
                        <div className="relative">
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchQuery}
                                onChange={(e) => handleSearch(e.target.value)}
                                placeholder="Cherchez un terme, un concept, une frustration..."
                                className="w-full px-6 py-4 bg-slate-900/90 backdrop-blur text-white text-lg border border-cyan-500/30 rounded-lg focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all placeholder:text-slate-500"
                            />
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1 text-[10px] font-mono text-slate-500 bg-slate-800 px-2 py-1 rounded border border-slate-700">
                                <Command size={10} /> K
                            </div>
                        </div>
                    </div>
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-fuchsia-400 to-transparent"></div>
            </header>

            <main className="relative max-w-7xl mx-auto px-6 py-12">
                {/* Alphabet */}
                <div className="mb-16">
                    <h2 className="text-base font-black mb-6 text-slate-300 font-mono flex items-center gap-3 uppercase tracking-wide">
                        <span className="text-cyan-400">&gt;</span> PAR LETTRE
                    </h2>
                    <div className="flex flex-wrap gap-3">
                        {letters.map((letter) => (
                            <button
                                key={letter}
                                onClick={() => handleLetterFilter(letter)}
                                className={`w-14 h-14 flex items-center justify-center font-bold text-lg rounded-lg border transition-all ${selectedLetter === letter
                                    ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                                    : 'bg-transparent border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-300'
                                    }`}
                            >
                                {letter}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Categories / Tags */}
                <div className="mb-16">
                    <div className="flex items-center gap-4 mb-6">
                        <h2 className="text-base font-black text-slate-300 font-mono flex items-center gap-3 uppercase tracking-wide">
                            <span className="text-cyan-400">&gt;</span> CATÉGORIES
                        </h2>
                        <div className="text-sm text-slate-500">
                            <span className="text-cyan-400 font-mono">{totalCount} termes</span> · {visibleCount} affichés
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={() => handleTagFilter('all')}
                            className={`px-6 py-2.5 text-sm font-medium rounded-lg border transition-all ${selectedTag === 'all'
                                ? 'bg-cyan-500/20 border-cyan-500 text-white'
                                : 'bg-transparent border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-300'
                                }`}
                        >
                            Tous
                        </button>
                        {tags.map((tag) => (
                            <button
                                key={tag}
                                onClick={() => handleTagFilter(tag)}
                                className={`px-6 py-2.5 text-sm font-medium rounded-lg border transition-all ${selectedTag === tag
                                    ? 'bg-cyan-500/20 border-cyan-500 text-white'
                                    : 'bg-transparent border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-300'
                                    }`}
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredData.map((item, idx) => {
                        const theme = getCardTheme(idx);
                        const themeColors = {
                            cyan: {
                                text: 'text-cyan-400',
                                border: 'border-cyan-500/30',
                                bg: 'bg-cyan-500/10',
                                cardBg: 'from-slate-900 via-slate-900 to-cyan-950/40',
                                cardBorder: 'border-cyan-500/20'
                            },
                            fuchsia: {
                                text: 'text-fuchsia-400',
                                border: 'border-fuchsia-500/30',
                                bg: 'bg-fuchsia-500/10',
                                cardBg: 'from-slate-900 via-slate-900 to-fuchsia-950/40',
                                cardBorder: 'border-fuchsia-500/20'
                            },
                            violet: {
                                text: 'text-violet-400',
                                border: 'border-violet-500/30',
                                bg: 'bg-violet-500/10',
                                cardBg: 'from-slate-900 via-slate-900 to-violet-950/40',
                                cardBorder: 'border-violet-500/20'
                            },
                        }[theme];

                        return (
                            <div
                                key={item.id}
                                onClick={() => onTermClick(item)}
                                className="group relative cursor-pointer"
                            >
                                <div className={`relative bg-gradient-to-br ${themeColors.cardBg} border ${themeColors.cardBorder} rounded-2xl p-8 h-full transition-all flex flex-col hover:border-opacity-40 overflow-hidden`}>
                                    {/* Halo lumineux radial en haut à droite */}
                                    <div
                                        className="absolute top-0 right-0 w-64 h-64 opacity-30 pointer-events-none"
                                        style={{
                                            background: `radial-gradient(circle at top right, ${theme === 'cyan' ? 'rgba(34, 211, 238, 0.4)' :
                                                    theme === 'fuchsia' ? 'rgba(217, 70, 239, 0.4)' :
                                                        'rgba(139, 92, 246, 0.4)'
                                                }, transparent 70%)`
                                        }}
                                    ></div>

                                    {/* Tag Pill */}
                                    <div className="mb-6">
                                        {item.tags.length > 0 && (
                                            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-mono font-black border uppercase tracking-wider ${themeColors.bg} ${themeColors.text} ${themeColors.border}`}>
                                                <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                                                {item.tags[0]}
                                            </div>
                                        )}
                                    </div>

                                    <div className="relative z-10 flex-1">
                                        <h3 className="text-3xl font-sans font-bold mb-2 leading-tight tracking-tight text-white">
                                            {item.term}
                                        </h3>

                                        <p className={`font-mono text-xs mb-6 tracking-[0.2em] font-bold ${themeColors.text}`}>
                                            {item.type}
                                        </p>

                                        <p className="text-slate-400 leading-relaxed text-lg font-light">
                                            {item.definition}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Empty state */}
                {filteredData.length === 0 && (
                    <div className="text-center py-32 border-2 border-dashed border-slate-900 rounded-3xl">
                        <div className="text-6xl mb-6">👽</div>
                        <h3 className="text-2xl font-bold text-slate-700 mb-2">ZONE_VIDE</h3>
                        <p className="text-slate-500 font-mono">Aucune donnée trouvée pour l'index spécifié.</p>
                    </div>
                )}
            </main>
        </div>
    );
}
